import type { EpisodeFiltersShape } from '@bastion/assistant';
import { isTaskRating, type EpisodeDisplayPreferences, type SubtaskCoverage } from '../types';
import type { FilterState } from '@bastion/list-view';

function getIsValues(state: FilterState, attributeId: string): string[] {
  const f = state.filters.find((x) => x.attributeId === attributeId);
  if (!f || f.operator !== 'is') {
    return [];
  }
  return f.values.filter((v) => v.length > 0);
}

function yesNoStringsToBooleans(values: string[]): boolean[] | null {
  const out: boolean[] = [];
  for (const v of values) {
    if (v === 'yes') {
      out.push(true);
    } else if (v === 'no') {
      out.push(false);
    }
  }
  return out.length > 0 ? out : null;
}

/**
 * Flatten list-view filter chips + display preferences into the assistant-facing
 * shape consumed by `buildAssistantAppState`.
 */
export function flattenEpisodeFiltersForAssistant(
  filterState: FilterState,
  display: EpisodeDisplayPreferences,
  currentPage: number,
): EpisodeFiltersShape {
  const sourceVals = getIsValues(filterState, 'source');
  const ratingVals = getIsValues(filterState, 'rating')
    .map((v) => Number(v))
    .filter(isTaskRating);
  const hostVals = getIsValues(filterState, 'hostname');
  const collectorVals = getIsValues(filterState, 'collector');
  const hardwareVals = getIsValues(filterState, 'hardware');
  const sopVals = getIsValues(filterState, 'sop');
  const tagVals = getIsValues(filterState, 'tag');
  const subVals = [
    ...getIsValues(filterState, 'subtask_coverage'),
    // Legacy saved URLs/views used `subtasks` for coverage.
    ...getIsValues(filterState, 'subtasks'),
  ].filter((v): v is SubtaskCoverage => v === 'full' || v === 'partial' || v === 'none');

  // `created` chip carries either a preset slug (single value) or absolute
  // from/to ISO strings (two values). Only the absolute form maps to the
  // legacy `dateFrom`/`dateTo` assistant fields. Preset slugs are skipped
  // here — the assistant doesn't have a vocabulary for "last 2 hours" yet.
  const createdF = filterState.filters.find(
    (x) => x.attributeId === 'created' && x.operator === 'is',
  );
  const isCreatedAbsolute = (createdF?.values.length ?? 0) === 2;
  const fromRaw = isCreatedAbsolute ? createdF?.values[0]?.trim() ?? '' : '';
  const toRaw = isCreatedAbsolute ? createdF?.values[1]?.trim() ?? '' : '';

  const searchF = filterState.filters.find((x) => x.attributeId === 'search' && x.operator === 'is');
  const search = searchF?.values[0]?.trim() ?? '';

  return {
    search,
    source: sourceVals.length > 0 ? sourceVals : '',
    rating: ratingVals.length > 0 ? ratingVals : null,
    hostname: hostVals.length > 0 ? hostVals : '',
    collectorId: collectorVals.length > 0 ? collectorVals : null,
    hardwareVersion: hardwareVals.length > 0 ? hardwareVals : null,
    sopId: sopVals.length > 0 ? sopVals : null,
    tag: tagVals.length > 0 ? tagVals : null,
    dateFrom: fromRaw.length > 0 ? fromRaw : null,
    dateTo: toRaw.length > 0 ? toRaw : null,
    reviewed: yesNoStringsToBooleans(getIsValues(filterState, 'reviewed')),
    hasInterventions: yesNoStringsToBooleans(getIsValues(filterState, 'interventions')),
    subtaskCoverage: subVals.length > 0 ? subVals : null,
    viewMode: display.viewMode,
    sort: display.sort,
    pageSize: display.pageSize,
    currentPage,
  };
}
